<template>
    <component
        v-if="isRoutable"
        :is="linkTag"
        v-bind="routingProps"
        v-slot="{ href, route, navigate, isActive, isExactActive }"
        custom
    >
        <li
            role="tab"
            :class="[$style['mds-tabs__item'], classObject, activeClass(isActive, isExactActive)]"
            @click="makeActive"
        >
            <mds-button
                :href="href"
                :on-dark="onDark"
                :icon="icon"
                :icon-right="iconRight"
                :size="size"
                :text="text"
                variation="flat"
                :type="typeAttribute"
                :target="target"
                @click="navigate"
            >
                <slot>{{ text }}</slot>
            </mds-button>
        </li>
    </component>
    <li v-else role="tab" :class="[$style['mds-tabs__item'], classObject]" @click="makeActive">
        <mds-button
            :href="href"
            :on-dark="onDark"
            :icon="icon"
            :icon-right="iconRight"
            :size="size"
            :text="text"
            variation="flat"
            :type="typeAttribute"
            :disabled="disabledAttribute"
            :target="target"
        >
            <slot>{{ text }}</slot>
        </mds-button>
    </li>
</template>

<script>
    import { MdsButton } from '@mds/button';
    import MdsRoutable from '@mds/routable';

    export default {
        name: 'MdsTabsItem',
        mixins: [MdsRoutable],
        components: {
            MdsButton,
        },
        props: {
            active: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies active styling to the tab item.',
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: "If `true`, disables the tab item. Note: This shouldn't be used if `href` is used.",
                },
            },
            href: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'When provided, makes the tabs item `a`-based and sets the `href` path.',
                },
            },
            icon: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the left icon.',
                },
            },
            iconRight: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the right icon.',
                },
            },
            target: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the `target` attribute.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the tab item text.',
                },
            },
        },
        inject: ['parentVariation'], // Properties that are provided via ancestor components https://vuejs.org/v2/api/#provide-inject
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-tabs__item--active']] = this.active;

                return obj;
            },
            disabledAttribute() {
                return this.href ? null : this.disabled ? true : null;
            },
            typeAttribute() {
                return this.href ? null : 'button';
            },
            size() {
                return this.parentVariation.size;
            },
            onDark() {
                return this.parentVariation.onDark;
            },
        },
        methods: {
            makeActive(event) {
                if (this.disabled) {
                    return;
                }

                this.$emit('mds-tabs-item-active', event);
            },
            activeClass(isActive, isExactActive) {
                const active = this.exact ? isExactActive : isActive;
                return active ? this.$style['mds-tabs__item--active'] : null;
            },
        },
    };
</script>

<style lang="scss" module>
    @import './tabs.scss';
</style>
